import { fromSpecific, scope, TypedNode } from '@rehaje/core'
import { reactive } from '@rehaje/reactive'

import type { SchemaArrayModel, SchemaObjectModel } from '../plugins'
import type {
  SchemaBooleanLikeNode,
  SchemaBooleanLikeSpecific,
  SchemaEventsSpecific,
  SchemaMessagesNode,
  SchemaMessagesSpecific,
  SchemaPathMode,
  SchemaTextNode,
  SchemaWidgets
} from '../types'
import type { SchemaModel } from './SchemaModel'
import type { WidgetNode } from './WidgetNode'
import type {
  SchemaBooleanSpecific,
  TypedSchema,
  TypedSpecific,
  ViewScope
} from '@rehaje/core'
import type { TRecord } from '@rehaje/shared'

const isSchemaObjectModel = (target: any): target is SchemaObjectModel =>
  target?.type === 'Schema.Object'

const isSchemaArrayModel = (target: any): target is SchemaArrayModel =>
  target?.type === 'Schema.Array'

const createProxy = (record: any, index: number, scope?: ViewScope) => {
  if (typeof record === 'object' && record !== null) {
    return new Proxy(record, {
      get(target, prop) {
        if (prop === '$index') return index
        if (prop === '$rollup') {
          return scope?.variables.$rollup
        }
        if (prop === '$lookup') return scope?.variables.$lookup
        return target[prop]
      },
      getOwnPropertyDescriptor(target, prop) {
        if (prop === '$index') return { value: index }
        if (prop === '$rollup') return { value: scope?.variables.$rollup }
        if (prop === '$lookup') return { value: scope?.variables.$lookup }
        return Object.getOwnPropertyDescriptor(target, prop)
      },
      has(target, prop) {
        if (prop === '$index') return true
        if (prop === '$rollup') return true
        if (prop === '$lookup') return true
        return prop in target
      },
      ownKeys(target) {
        return Object.keys(target).concat('$index', '$rollup', '$lookup')
      }
    })
  }
  return record
}

const createRecord = (node: SchemaNode, name: string) => {
  const scope = node.getSelfViewScope()
  const index = scope?.getLocalVariable('$index', true)
  const model = scope?.model as SchemaModel | undefined
  let objParent = model?.valuableParent
  if (isSchemaObjectModel(objParent) && typeof objParent.value === 'object') {
    return createProxy(objParent.value, index, scope)
  }

  if (isSchemaArrayModel(objParent)) {
    const belongs = node.getBelongs()
    if (belongs[0] === 'items') {
      const record = scope?.getLocalVariable(name, true)
      if (record) return createProxy(record, index, scope)
    }
  }

  while (!isSchemaObjectModel(objParent)) {
    objParent = objParent?.valuableParent
  }
  return createProxy(objParent?.value ?? {}, index, scope)
}

@scope({
  type: 'Local',
  variables: [
    {
      get() {
        return this.getViewModel()
      },
      name() {
        return this.id
      },
      type: 'Block'
    },
    {
      get() {
        return this.getViewModel()
      },
      name: '$self',
      type: 'Local'
    },
    {
      get() {
        return this.getViewModel<SchemaModel>()?.parent
      },
      name: '$parent',
      type: 'Local'
    },
    {
      get() {
        return this.getViewModel<SchemaModel>()?.form
      },
      name: '$form',
      type: 'Local'
    },
    {
      get() {
        return this.getViewModel<SchemaModel>()?.form?.value
      },
      name: '$values',
      type: 'Local'
    },
    {
      get() {
        return createRecord(this, '$record')
      },
      name: '$record',
      type: 'Local'
    },
    /**
     * @deprecated
     */
    {
      get() {
        return createRecord(this, '$realRecord')
      },
      name: '$realRecord',
      type: 'Local'
    },
    {
      get() {
        return this.getSelfViewScope()?.getLocalVariable('$records', true) ?? []
      },
      name: '$records',
      type: 'Local'
    },
    {
      get() {
        return this.getSelfViewScope()?.getLocalVariable('$index', true)
      },
      name: '$index',
      type: 'Local'
    },
    {
      get() {
        const parentVariables = this.getParent()?.getViewScope().variables
        if (parentVariables) {
          return {
            get $index() {
              return parentVariables.$index
            },
            get $realRecord() {
              return parentVariables.$realRecord
            },
            get $record() {
              return parentVariables.$record
            },
            get $rollup() {
              return parentVariables.$rollup
            }
          }
        }
      },
      name: '$rollup',
      type: 'Local'
    },
    /**
     * @deprecated
     */
    {
      get() {
        const parentVariables = this.getParent()?.getViewScope().variables
        if (parentVariables) {
          return {
            get $index() {
              return parentVariables.$index
            },
            get $lookup() {
              return parentVariables.$lookup
            },
            get $realRecord() {
              return parentVariables.$realRecord
            },
            get $record() {
              return parentVariables.$record
            }
          }
        }
      },
      name: '$lookup',
      type: 'Local'
    }
  ]
})
export class SchemaNode extends TypedNode {
  /**
   * 输入控件
   */
  @fromSpecific()
  @reactive()
  component?: WidgetNode

  /**
   * 输入控件包装器
   */
  @fromSpecific()
  @reactive()
  decorator?: WidgetNode

  /**
   * 描述信息
   */
  @fromSpecific()
  @reactive()
  description?: SchemaTextNode

  /**
   * 禁用
   */
  @fromSpecific()
  @reactive()
  disabled?: SchemaBooleanLikeNode

  /**
   * 错误信息
   */
  @fromSpecific()
  @reactive()
  errors?: SchemaMessagesNode

  /**
   * 事件
   */
  @fromSpecific()
  events: SchemaEventsSpecific = {}

  /**
   * 扩展数据
   */
  @fromSpecific()
  @reactive()
  extra?: TRecord

  /**
   * 是否隐藏
   */
  @fromSpecific()
  @reactive()
  hidden?: SchemaBooleanLikeNode

  /**
   * 表单项名称
   */
  @fromSpecific()
  @reactive()
  name: PropertyKey = this.id

  /**
   * 数据路径解析模式
   * - 'struct': 结构化模式 (a.b -> {a:{b:..}})
   * - 'literal': 字面量模式 (a.b -> {"a.b":..})
   */
  @fromSpecific()
  @reactive()
  pathMode?: SchemaPathMode

  /**
   * 是否为只读态
   */
  @fromSpecific()
  @reactive()
  readOnly?: SchemaBooleanLikeNode

  /**
   * 是否为阅读态
   */
  @fromSpecific()
  @reactive()
  readPretty?: SchemaBooleanLikeNode

  /**
   * 显示时自动恢复值，与 removeValueOnHidden 配合使用
   */
  @fromSpecific()
  @reactive()
  recoverValueOnShown?: SchemaBooleanLikeNode

  /**
   * 隐藏时自动删值
   */
  @fromSpecific()
  @reactive()
  removeValueOnHidden?: SchemaBooleanLikeNode

  /**
   * 是否必填
   */
  @fromSpecific()
  @reactive()
  required?: SchemaBooleanLikeNode

  /**
   * @deprecated
   */
  @fromSpecific()
  @reactive()
  requiredMessage?: SchemaMessagesNode

  /**
   * schema 原始协议
   */
  @reactive()
  schema: TypedSchema

  /**
   * 成功提示
   */
  @fromSpecific()
  @reactive()
  successes?: SchemaMessagesNode

  /**
   * 标题
   */
  @fromSpecific()
  @reactive()
  title?: SchemaTextNode

  /**
   * Hook 挂载器
   */
  @fromSpecific()
  @reactive()
  use: TypedNode[] = []

  /**
   * 校验器
   * @deprecated
   */
  @fromSpecific()
  @reactive()
  validator?: (value: any, values: any) => string | undefined

  /**
   * 局部变量
   */
  @fromSpecific()
  @reactive()
  variables: TRecord = {}

  /**
   * 是否显示
   * 包含 hidden + removeValueOnHidden 的功能
   * @deprecated
   */
  @fromSpecific()
  @reactive()
  visible!: SchemaBooleanLikeNode

  /**
   * 警告信息
   */
  @fromSpecific()
  @reactive()
  warnings?: SchemaMessagesNode

  /**
   * 是否可编辑
   */
  @fromSpecific()
  @reactive()
  writeOnly: SchemaBooleanLikeNode = true

  constructor(specific: TypedSchema) {
    super(specific)
    this.schema = specific
  }

  override transpileSlave() {
    return this.render()
  }
}

declare global {
  export interface RehajeSchemaMetadata {
    /**
     * 输入控件
     */
    component?: SchemaWidgets
    /**
     * 输入控件包装器
     */
    decorator?: SchemaWidgets
    /**
     * 禁用
     */
    disabled?: SchemaBooleanLikeSpecific
    /**
     * 错误消息
     */
    errors?: SchemaMessagesSpecific
    /**
     * 事件
     */
    events?: SchemaEventsSpecific
    /**
     * 扩展数据
     */
    extra?: TRecord
    /**
     * 是否隐藏
     */
    hidden?: SchemaBooleanLikeSpecific
    /**
     * 数据路径解析模式
     * @default 'struct'
     * - 'struct': 结构化模式 (a.b -> {a:{b:..}})
     * - 'literal': 字面量模式 (a.b -> {"a.b":..})
     */
    pathMode?: SchemaPathMode
    /**
     * 阅读态
     */
    readPretty?: SchemaBooleanSpecific
    /**
     * 显示时自动恢复值，与 removeValueOnHidden 配合使用
     */
    recoverValueOnShown?: SchemaBooleanLikeSpecific
    /**
     * 隐藏时自动删值
     */
    removeValueOnHidden?: SchemaBooleanLikeSpecific
    /**
     * 成功提示
     */
    successes?: SchemaMessagesSpecific
    /**
     * Hooks 挂载器
     */
    use?: TypedSpecific[]
    /**
     * 校验器
     * @deprecated
     */
    validator?: (value: any, values: any) => string | undefined
    /**
     * 局部变量
     */
    variables?: TRecord
    /**
     * 是否显示
     * @deprecated
     */
    visible?: SchemaBooleanLikeSpecific
    /**
     * 警告提示
     */
    warnings?: SchemaMessagesSpecific
  }
}
